// part of nuxt
import type { $Fetch, NitroFetchRequest } from "nitropack"
import { TYPESENSE_REQUEST_TIMEOUT_MS } from "@/utils/typesenseRetry"

interface GetOffersParams {
  // Preset text query. Omitted/empty → wildcard ("*") to match all offers.
  query?: string
  // Typesense `filter_by` clause (e.g. a search module's preset filters).
  filterBy?: string
  // Hits per page (Typesense max is 250).
  perPage?: number
  // 1-based page number.
  page?: number
  // Typesense `include_fields` — comma-separated fields returned per hit.
  // Defaults to "url,title" for the crawler link list.
  includeFields?: string
}

// Fields query_by-searched when a preset text query is present. Excludes the
// `title_embed` vector field on purpose — the crawler link list needs the set
// of matching offers, not semantic ranking, and vector search is the expensive
// part of the live search pipeline. (JET-1826)
const OFFER_QUERY_BY = "title,location,company,full_address,external_id"

// Newest offers first, so the most recent jobs lead the crawler link list.
const OFFER_SORT_BY = "create_date_timestamp:desc"

export function typesenseRepository<T>(fetch: $Fetch<T, NitroFetchRequest>) {
  return {
    /**
     * Returns job count from Typesense
     * @param tenantId targeted tenant
     * @param jobShopId id of the JobShop from which data is requested
     * @param typesenseApiKey API key for Typesense
     * @param typesenseUrl URL of the Typesense instance
     * @returns
     */
    async getJobCount(tenantId: string, jobShopId: string, typesenseApiKey: string, typesenseUrl: string): Promise<TypesenseResponse> {
      return fetch<TypesenseResponse>(`${typesenseUrl}/multi_search`, {
        method: "POST",
        timeout: TYPESENSE_REQUEST_TIMEOUT_MS,
        headers: {
          "X-Tenant-Id": tenantId,
          "X-JobShop-Id": jobShopId,
          "X-Typesense-Api-Key": typesenseApiKey,
        },
        body: JSON.stringify({
          searches: [{
            facet_by: "department",
            collection: "offers",
            q: "*",
            page: 1,
            max_facet_values: 1000,
          }],
        }),
      })
    },

    /**
     * Returns offers reduced to the fields needed to render crawlable job links
     * (`url` + `title`). Deliberately lean: only two fields and no semantic/vector
     * ranking, so it stays cheap even for jobshops with thousands of offers. Used
     * for the SSR/no-JS crawler fallback (see useSsrJobLinks). (JET-1826)
     * @param tenantId targeted tenant
     * @param jobShopId id of the JobShop from which data is requested
     * @param typesenseApiKey API key for Typesense
     * @param typesenseUrl URL of the Typesense instance
     * @param params query / filter_by / paging options
     * @returns the Typesense multi_search response (hits carry only url + title)
     */
    async getOffers(tenantId: string, jobShopId: string, typesenseApiKey: string, typesenseUrl: string, params: GetOffersParams = {}): Promise<TypesenseResponse> {
      const query = params.query || "*"

      const search: Record<string, unknown> = {
        collection: "offers",
        q: query,
        include_fields: params.includeFields ?? "url,title",
        filter_by: params.filterBy ?? "",
        sort_by: OFFER_SORT_BY,
        per_page: params.perPage ?? 250,
        page: params.page ?? 1,
      }

      // query_by is required by Typesense only for a real text query; a wildcard
      // ("*") matches all offers without it.
      if (query !== "*")
        search.query_by = OFFER_QUERY_BY

      return fetch<TypesenseResponse>(`${typesenseUrl}/multi_search`, {
        method: "POST",
        timeout: TYPESENSE_REQUEST_TIMEOUT_MS,
        headers: {
          "X-Tenant-Id": tenantId,
          "X-JobShop-Id": jobShopId,
          "X-Typesense-Api-Key": typesenseApiKey,
        },
        body: JSON.stringify({ searches: [search] }),
      })
    },
  }
}
